/**
 * Locale-aware formatting utilities.
 * Uses i18n.language as the locale for Intl APIs so that
 * dates/numbers/currency follow the user's selected language.
 */
import i18n from '@/i18n/config';

// ============================================================================
// Locale
// ============================================================================

/** Returns the current i18n language (e.g. 'en-US', 'vi-VN'). */
export const getLocale = (): string => i18n.language || 'en-US';

// ============================================================================
// Date Formatting
// ============================================================================

const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

const DEFAULT_DATETIME_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

/**
 * Format a date string/Date using the current i18n locale.
 * Returns '--' for null/undefined input.
 */
export const formatLocalDate = (
  date: Date | string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (date == null) return '—';
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString(getLocale(), options ?? DEFAULT_DATE_OPTIONS);
  } catch {
    return typeof date === 'string' ? date : '—';
  }
};

/**
 * Format a date+time string/Date using the current i18n locale.
 * Returns '--' for null/undefined input.
 */
export const formatLocalDateTime = (
  date: Date | string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (date == null) return '—';
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString(getLocale(), options ?? DEFAULT_DATETIME_OPTIONS);
  } catch {
    return typeof date === 'string' ? date : '—';
  }
};

// ============================================================================
// Number / Currency Formatting
// ============================================================================

/**
 * Format a number as currency using the current i18n locale.
 * Defaults to USD if no currency specified.
 */
export const formatLocalCurrency = (amount: number, currency = 'USD'): string => {
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency,
  }).format(amount);
};

// ============================================================================
// User Preference Date Formatting (pattern + timezone offset)
// ============================================================================

// Sentinel: -999 = use browser local time (no offset applied)
const NO_OFFSET = -999;

/**
 * Format a UTC date from the server → local display string.
 * @param date           - UTC date (string or Date)
 * @param pattern        - 'dd/MM/yyyy' | 'MM/dd/yyyy' | 'yyyy-MM-dd'
 * @param timezoneOffset - seconds; UTC+7 = 25200, UTC-5 = -18000, -999 = browser default
 */
export const formatDateByPattern = (
  date: Date | string | null | undefined,
  pattern: string,
  timezoneOffset: number,
): string => {
  if (date == null) return '—';
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return '—';
    const pad = (n: number) => String(n).padStart(2, '0');
    let year: number, month: number, day: number;
    if (timezoneOffset === NO_OFFSET) {
      // Use browser local getters — no manual shift needed
      year = d.getFullYear();
      month = d.getMonth() + 1;
      day = d.getDate();
    } else {
      // Shift UTC → target timezone: offset in seconds → ms
      const local = new Date(d.getTime() + timezoneOffset * 1000);
      year = local.getUTCFullYear();
      month = local.getUTCMonth() + 1;
      day = local.getUTCDate();
    }
    return pattern
      .replace('yyyy', String(year))
      .replace('MM', pad(month))
      .replace('dd', pad(day));
  } catch {
    return '—';
  }
};

/**
 * Convert local date input → UTC ISO string for POST to server.
 * Symmetric with formatDateByPattern: subtract offset to get UTC.
 * @param date           - local date string or Date
 * @param timezoneOffset - seconds (same unit as formatDateByPattern)
 */
export const localToUtcIso = (
  date: string | Date,
  timezoneOffset: number,
): string => {
  // Parse strings without timezone info as UTC (naive datetime in user's preference tz)
  // to avoid double-offset from browser's local timezone.
  const naiveMs =
    typeof date === 'string'
      ? new Date(/Z|[+-]\d{2}:\d{2}$/.test(date) ? date : date + 'Z').getTime()
      : date.getTime();
  return new Date(naiveMs - timezoneOffset * 1000).toISOString();
};

/**
 * Format a date using user preferences.
 * Falls back to formatLocalDate when dateFormat is empty (system default).
 */
export const formatDateByPrefs = (
  date: Date | string | null | undefined,
  dateFormat: string,
  timezoneOffset: number,
): string => {
  if (!dateFormat) return formatLocalDate(date);
  return formatDateByPattern(date, dateFormat, timezoneOffset);
};

/**
 * Format a date+time using user timezone preference (IANA name).
 * Falls back to formatLocalDateTime when timezone is 'Browser/Default' or empty.
 */
export const formatDateTimeByPrefs = (
  date: Date | string | null | undefined,
  timezone: string,
): string => {
  if (!timezone || timezone === 'Browser/Default') return formatLocalDateTime(date);
  return formatLocalDateTime(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  });
};
